import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  formaterDate,
  formaterMontant,
  formaterPourcentage,
  formaterQuantite,
  montant,
  somme,
} from '@gsm/partage';
import { api, ErreurApi } from '../api/client';
import { messageErreur, useRessource } from '../api/ressource';
import { useAuth } from '../auth/contexte';
import { Bloc, Tableau, type Colonne } from '../composants/Tableau';
import { BandeauAlerte } from '../composants/BandeauAlerte';
import { Pastille } from '../composants/Pastille';
import { Nombre } from '../composants/Nombre';
import { Note } from '../composants/Note';

interface OrdrePropose {
  canal: string;
  sens: string;
  mode: string;
  instrumentId: string | null;
  trancheMaturiteId: string | null;
  libelle: string;
  instrumentPropose: boolean;
  quantite: string | null;
  montant: string | null;
  coursIndicatif: string | null;
  montantEstime: string;
  horsCible: boolean;
}

interface Sortie {
  id: string;
  reference: string;
  montantDemande: string;
  totalite: boolean;
}

/**
 * Ce que le serveur a retenu, et d'où il le tient.
 *
 * Aucun montant ne remonte de l'écran : la trésorerie vient de la situation
 * reçue, le désinvestissement de la sortie de fonds ouverte.
 */
interface IntentionRecue {
  origine: string;
  mouvementRetenu: string;
  simulation: boolean;
  demandeSortie: Sortie | null;
  tresorerie: {
    solde: string;
    soldeProjete: string;
    tamponCible: string;
    montantInvestissable: string;
    seuil: string;
    dateSituation: string | null;
    situationPerimee: boolean;
  };
}

interface Proposition {
  mode: string;
  intention: IntentionRecue;
  actifNet: string;
  assiette: string;
  tresorerieCible: string;
  tresoreriePrevisionnelle: string;
  actifNetPrevisionnel: string;
  avertissements: string[];
  alertes: { code: string; message: string; montant: string | null }[];
  ecarts: {
    libelle: string;
    poche: string | null;
    valeurCible: string;
    valeurActuelle: string;
    ecart: string;
    retenu: boolean;
    horsCible: boolean;
  }[];
  ordres: OrdrePropose[];
}

interface Mandat {
  reference: string;
  statut: string;
  client: { nom: string; prenom: string };
  profil: { libelle: string };
  tamponTresoreriePct: string;
}

/** Une alerte de gestion n'est pas un refus : elle s'affiche en ambre. */
const ALERTES_REFUS = new Set(['TRESORERIE_INSUFFISANTE', 'CHARIA_NON_CONFORME']);

/** Ce que l'intention retenue par le serveur donne à lire au gérant. */
const NATURE: Record<string, string> = {
  INVESTISSEMENT: 'Investissement',
  DESINVESTISSEMENT: 'Désinvestissement',
  LIQUIDATION: 'Liquidation totale',
};

export function Proposition() {
  const { id } = useParams<{ id: string }>();
  const naviguer = useNavigate();
  const { aRole } = useAuth();
  const mandat = useRessource<Mandat>(`/mandats/${id}`);

  const [proposition, setProposition] = useState<Proposition | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [occupe, setOccupe] = useState(false);
  const [enregistrement, setEnregistrement] = useState(false);

  const intention = proposition?.intention ?? null;
  const sortie = intention?.demandeSortie ?? null;
  const peutGenerer = aRole('GERANT', 'RESP_GESTION');

  /**
   * Demande une proposition. Aucun montant ne part d'ici : le serveur le dérive
   * de la situation reçue et de la sortie de fonds ouverte, puis dit dans sa
   * réponse ce qu'il a retenu.
   */
  async function calculer(simulation?: 'LIQUIDATION') {
    setErreur(null);
    setProposition(null);
    setOccupe(true);
    try {
      setProposition(
        await api.post<Proposition>(
          `/portefeuilles/${id}/proposition`,
          simulation ? { simulation } : {},
        ),
      );
    } catch (e) {
      setErreur(e instanceof ErreurApi ? messageErreur(e) : 'Calcul impossible.');
    } finally {
      setOccupe(false);
    }
  }

  /**
   * Matérialise la proposition en ordres.
   *
   * La référence de la sortie de fonds accompagne la demande : elle n'apporte
   * aucun montant — le serveur le relit — mais atteste que le gérant a bien vu
   * la demande qu'il va servir.
   */
  async function materialiser() {
    setErreur(null);
    setEnregistrement(true);
    try {
      await api.post('/ordres/generer', {
        mandatId: id,
        ...(sortie && { demandeSortieId: sortie.id }),
      });
      naviguer('/ordres');
    } catch (e) {
      setErreur(e instanceof ErreurApi ? messageErreur(e) : 'Génération impossible.');
    } finally {
      setEnregistrement(false);
    }
  }

  const m = mandat.donnees;
  const totalSens = (sens: string) =>
    somme(
      (proposition?.ordres ?? [])
        .filter((o) => o.sens === sens)
        .map((o) => montant(o.montantEstime)),
    );
  const totalAchats = totalSens('ACHAT');
  const totalVentes = totalSens('VENTE');
  const aConfirmer = (proposition?.ordres ?? []).filter((o) => o.instrumentPropose).length;

  const colonnes: Colonne<OrdrePropose>[] = [
    {
      cle: 'sens',
      entete: 'Sens',
      rendu: (o) => (
        <span className={o.sens === 'ACHAT' ? 'ordre-achat' : 'ordre-vente'}>{o.sens}</span>
      ),
    },
    { cle: 'canal', entete: 'Canal', rendu: (o) => o.canal.replace('_', ' ').toLowerCase() },
    {
      cle: 'libelle',
      entete: 'Sous-jacent',
      rendu: (o) => (
        <>
          {o.libelle}
          {o.instrumentPropose && (
            <>
              {' '}
              <Pastille ton="attente">Fonds à confirmer</Pastille>
            </>
          )}
          {o.horsCible && (
            <>
              {' '}
              <Pastille ton="neutre">Hors cible</Pastille>
            </>
          )}
        </>
      ),
    },
    {
      cle: 'detail',
      entete: 'Quantité / montant',
      num: true,
      rendu: (o) =>
        o.quantite ? `${formaterQuantite(o.quantite)} titres` : `${formaterMontant(o.montant)} MAD`,
    },
    {
      cle: 'cours',
      entete: 'Cours',
      num: true,
      rendu: (o) => (o.coursIndicatif ? formaterMontant(o.coursIndicatif) : '—'),
    },
    {
      cle: 'montant',
      entete: 'Montant estimé',
      num: true,
      rendu: (o) => <Nombre valeur={o.montantEstime} />,
    },
  ];

  if (mandat.chargement) return <p className="surtitre">Chargement…</p>;
  if (!m)
    return (
      <BandeauAlerte nature="refus" titre="Mandat introuvable">
        {messageErreur(mandat.erreur) ?? 'Ce mandat n’existe pas.'}
      </BandeauAlerte>
    );

  return (
    <>
      <p className="entete__fil">
        <Link to="/tresorerie?oisifs=1">Trésorerie</Link> ›{' '}
        <Link to={`/mandats/${id}`}>{m.reference}</Link> › Proposition
      </p>
      <div className="entete">
        <h1>
          {m.client.prenom} {m.client.nom}
        </h1>
        <span className="entete__ref">{m.reference}</span>
        <Pastille ton="neutre">{m.profil.libelle}</Pastille>
        {sortie && <Pastille ton="attente">{sortie.reference}</Pastille>}
      </div>

      <Bloc titre="Calculer une proposition" meta="aucune écriture, le gérant ajuste ensuite">
        <div className="rangee-actions">
          <button className="bouton" disabled={occupe} onClick={() => void calculer()}>
            {occupe ? 'Calcul…' : 'Calculer'}
          </button>
          <button
            className="bouton bouton--fantome"
            disabled={occupe}
            onClick={() => void calculer('LIQUIDATION')}
          >
            Simuler une liquidation totale
          </button>
        </div>
        <Note resume="Le montant ne se saisit pas : il vient de la situation reçue et de la sortie de fonds ouverte.">
          À investir, la trésorerie qui excède le tampon ; à dégager, le montant de la sortie de
          fonds ouverte s’il y en a une. L’annoncer une seconde fois compterait l’argent deux fois.
        </Note>
      </Bloc>

      {intention && (
        <section className="cles" aria-label="Intention retenue">
          <div className="cle">
            <span className="surtitre">Nature</span>
            <div className="cle__valeur">
              {NATURE[intention.origine] ?? intention.origine}
              {intention.simulation && <small>simulation</small>}
            </div>
          </div>
          <div className="cle">
            <span className="surtitre">Mouvement retenu</span>
            <div className="cle__valeur">
              {/* Jamais coloré : un désinvestissement est une instruction, pas
                  une contre-performance (charte, § 2). */}
              <Nombre valeur={intention.mouvementRetenu} />
              <small>MAD</small>
            </div>
          </div>
          <div className="cle">
            {/* Face à une sortie, le solde projeté est déjà amputé du montant à
                libérer : afficher un « à placer » négatif n'apprendrait rien. */}
            <span className="surtitre">{sortie ? 'À dégager' : 'À placer au-delà du tampon'}</span>
            <div className="cle__valeur">
              <Nombre
                valeur={sortie ? sortie.montantDemande : intention.tresorerie.montantInvestissable}
              />
              <small>MAD</small>
            </div>
          </div>
          <div className="cle">
            <span className="surtitre">Situation au</span>
            <div className="cle__valeur">
              {intention.tresorerie.dateSituation
                ? formaterDate(intention.tresorerie.dateSituation)
                : '—'}
              {intention.tresorerie.situationPerimee && <small>périmée</small>}
            </div>
          </div>
        </section>
      )}

      {sortie && (
        <BandeauAlerte nature="gestion" titre={`Sortie de fonds ${sortie.reference}`}>
          {sortie.totalite
            ? 'Liquidation totale du mandat : toutes les positions sont soldées.'
            : `${formaterMontant(sortie.montantDemande)} MAD à dégager.`}{' '}
          La somme ne sortira qu’au paiement, constaté dans la situation reçue.
        </BandeauAlerte>
      )}

      {erreur && (
        <BandeauAlerte nature="refus" titre="Génération refusée">
          {erreur}
        </BandeauAlerte>
      )}

      {proposition && (
        <div className="colonnes">
          <section>
            <Bloc titre="Ordres proposés" meta={`mode ${proposition.mode.toLowerCase()}`}>
              <Tableau
                colonnes={colonnes}
                lignes={proposition.ordres}
                cle={(_, i) => String(i)}
                vide="Aucun ordre : le portefeuille est déjà conforme à sa cible."
              />
            </Bloc>

            <Bloc titre="Écarts à la cible" meta="valeur cible contre valeur actuelle">
              <Tableau
                colonnes={[
                  { cle: 'poche', entete: 'Poche', rendu: (e) => e.poche ?? 'hors cible' },
                  { cle: 'libelle', entete: 'Ligne', rendu: (e) => e.libelle },
                  {
                    cle: 'cible',
                    entete: 'Cible',
                    num: true,
                    rendu: (e) => <Nombre valeur={e.valeurCible} />,
                  },
                  {
                    cle: 'actuel',
                    entete: 'Actuel',
                    num: true,
                    rendu: (e) => <Nombre valeur={e.valeurActuelle} />,
                  },
                  {
                    cle: 'ecart',
                    entete: 'Écart',
                    num: true,
                    // Pas de couleur : un écart à la cible n'est pas une
                    // performance (charte, § 2), le signe suffit.
                    rendu: (e) => <Nombre valeur={e.ecart} />,
                  },
                  {
                    cle: 'retenu',
                    entete: 'Retenu',
                    rendu: (e) =>
                      e.retenu ? (
                        <Pastille ton="interface">Oui</Pastille>
                      ) : (
                        <Pastille ton="neutre">Sous seuil</Pastille>
                      ),
                  },
                ]}
                lignes={proposition.ecarts}
                cle={(_, i) => String(i)}
              />
            </Bloc>
          </section>

          <aside className="panneau">
            <div className="panneau-tete">
              <h2>Récapitulatif</h2>
              <p>Tampon de trésorerie {formaterPourcentage(m.tamponTresoreriePct)}</p>
            </div>
            <div className="panneau-corps">
              <div className="totaux">
                <div>
                  <span>Actif net</span>
                  <Nombre valeur={proposition.actifNet} />
                </div>
                <div>
                  <span>Assiette d’allocation</span>
                  <Nombre valeur={proposition.assiette} />
                </div>
                <div>
                  <span>Tampon cible</span>
                  <Nombre valeur={proposition.tresorerieCible} />
                </div>
                <div>
                  <span>Total achats</span>
                  <Nombre valeur={totalAchats} />
                </div>
                <div>
                  <span>Total ventes</span>
                  <Nombre valeur={totalVentes} />
                </div>
                <div>
                  <span>Trésorerie après</span>
                  <Nombre valeur={proposition.tresoreriePrevisionnelle} />
                </div>
                {/* Un achat convertit de la trésorerie en titres, une vente
                    l'inverse : cette ligne doit égaler l'actif net. */}
                <div>
                  <span>Actif net après exécution</span>
                  <Nombre valeur={proposition.actifNetPrevisionnel} />
                </div>
              </div>

              <div className="engage">
                <span className="surtitre">Montant engagé</span>
                <b>{formaterMontant(totalAchats.plus(totalVentes))}</b>
              </div>

              <button
                className="bouton bouton--pleine-largeur"
                disabled={!peutGenerer || enregistrement || proposition.ordres.length === 0}
                onClick={() => void materialiser()}
              >
                {enregistrement ? 'Génération…' : 'Générer les ordres'}
              </button>
              <p className="note-blocage">
                {!peutGenerer
                  ? 'Génération réservée au gérant et au responsable de gestion.'
                  : proposition.ordres.length === 0
                    ? 'Aucun ordre à générer.'
                    : aConfirmer > 0
                      ? `Les ordres partiront en brouillon ; ${aConfirmer} ordre(s) OPCVM y attendront la confirmation du fonds.`
                      : 'Les ordres partiront en brouillon, ajustables avant soumission.'}
              </p>
            </div>
          </aside>
        </div>
      )}

      {proposition && proposition.alertes.length > 0 && (
        <Bloc titre="Alertes de gestion" meta={`${proposition.alertes.length}`}>
          {proposition.alertes.map((a, i) => (
            <BandeauAlerte
              key={i}
              nature={ALERTES_REFUS.has(a.code) ? 'refus' : 'gestion'}
              titre={a.code.replace(/_/g, ' ')}
            >
              {a.message}
            </BandeauAlerte>
          ))}
        </Bloc>
      )}
    </>
  );
}
